import { FunctionComponent, h } from 'preact';
import Translate from '../translate/translate';
import { useCallback, useState } from 'preact/hooks';
import { ContactNotificationsOptions, OptInOutData } from '../../types/shared-location.types';
import { isNil as _isNil } from 'lodash';
import ApproveTermsAndConditions from './approve-terms-and-conditions/approve-terms-and-conditions';
import { useStore } from '../../helpers/use-store';
import { get as _get } from 'lodash';
import { useTheme } from '../../hooks/use-theme/use-theme';

interface Props {
	onChange: (contactNotifications: ContactNotificationsOptions) => void;
	onTermsChange: (approveTermsAndConditions: boolean) => void;
	optInOutData: OptInOutData;
}

const contactNotificationOptions = [
	{
		key: 'allow_send_email',
		dataTestId: 'contact-notifications-email',
		translation: 'SCHEDULE.EMAIL_UPDATES'
	},
	{ key: 'allow_send_sms', dataTestId: 'contact-notifications-sms', translation: 'SCHEDULE.SMS_UPDATES' }
];

const ContactNotifications: FunctionComponent<Props> = ({ onChange, onTermsChange, optInOutData }) => {
	const { allow_send_email, allow_send_sms, terms_and_conditions_text } = optInOutData;
	const [contactNotifications, setContactNotifications] = useState<ContactNotificationsOptions>({
		allow_send_sms,
		allow_send_email
	});
	const [approveTermsAndConditions, setApproveTermsAndConditions] = useState<boolean>(false);
	const [disableAllOptions, setDisableAllOptions] = useState<boolean>(false);

	const { customerExperienceStore } = useStore();

	const declineTermsAndConditions = useCallback(() => {
		setApproveTermsAndConditions(false);
		onTermsChange(false);
	}, [onTermsChange]);

	const updateKey = useCallback(
		(key: string) =>
			({ target }) => {
				const updatedContactNotifications = { ...contactNotifications, [key]: target.checked };

				setDisableAllOptions(false);

				setContactNotifications(updatedContactNotifications);
				onChange(updatedContactNotifications);

				if (_isNil(terms_and_conditions_text)) {
					return;
				}

				const shouldApproveTerms = Object.values(updatedContactNotifications).includes(true);

				if (!shouldApproveTerms) {
					declineTermsAndConditions();
				}
			},
		[contactNotifications, onChange, terms_and_conditions_text, declineTermsAndConditions]
	);

	const disableAll = useCallback(
		({ target }) => {
			const allOptionsDisabled = { allow_send_sms: false, allow_send_email: false };

			if (target.checked) {
				setContactNotifications(allOptionsDisabled);
				onChange(allOptionsDisabled);
			}

			setDisableAllOptions(target.checked);

			if (!_isNil(terms_and_conditions_text)) {
				declineTermsAndConditions();
			}
		},
		[onChange, terms_and_conditions_text, declineTermsAndConditions]
	);

	const approveTermsAndConditionsChange = useCallback(
		(approveTermsAndConditions: boolean) => {
			onTermsChange(approveTermsAndConditions);
			setApproveTermsAndConditions(approveTermsAndConditions);
		},
		[onTermsChange]
	);

	const { primaryColor } = useTheme();

	const shouldApproveTerms = Object.values(contactNotifications).includes(true);
	const shouldDisableNotification = _get(customerExperienceStore.customerExperience, 'isUserAuthenticated');

	return (
		<div className="contact-notifications" data-testid="contact-notifications">
			<div className="contact-notifications-title">
				<Translate text="SCHEDULE.DELIVERY_STATUS_UPDATES" />
			</div>

			<div className="contact-notifications-instructions">
				<label class="radio-text-container">
					<input class="radio-button" name="notifications-enabled" type="radio" checked={true} />
					<Translate text="SCHEDULE.STATUS_UPDATE_IN_THE_FOLLOWING" />
				</label>
			</div>

			<div className="contact-notifications-checkboxes">
				<div className="contact-notifications-options">
					{contactNotificationOptions.map(({ key, dataTestId, translation }) => (
						<div className="contact-notifications-option-container">
							<label className="checkbox-text-container">
								<input
									type="checkbox"
									style={{ accentColor: primaryColor }}
									className="contact-notifications-option optin-checkbox"
									checked={contactNotifications[key]}
									data-testid={dataTestId}
									onChange={updateKey(key)}
									disabled={shouldDisableNotification}
								/>
								<Translate text={translation} />
							</label>
						</div>
					))}
				</div>
			</div>
			<div className="contact-notifications-instructions">
				<label className="radio-text-container">
					<input
						type="radio"
						name="notifications-enabled"
						className="radio-button"
						checked={disableAllOptions}
						data-testid="contact-notifications-disable-all"
						onChange={disableAll}
						disabled={shouldDisableNotification}
					/>
					<span class="radio-text">
						<Translate text="SCHEDULE.DISABLE_UPDATES" />
					</span>
				</label>
			</div>

			{terms_and_conditions_text && (
				<ApproveTermsAndConditions
					isDisabled={!shouldApproveTerms || shouldDisableNotification}
					isChecked={approveTermsAndConditions}
					termsAndConditionsText={terms_and_conditions_text}
					onChange={approveTermsAndConditionsChange}
					className="contact-notifications-option-container terms-and-conditions"
				/>
			)}
		</div>
	);
};

export default ContactNotifications;
